import axios from 'axios';
import { getAuthToken } from '../utils/auth';
import API_CONFIG from '../config/api.config';

// Create an axios instance with default config
const httpClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add a request interceptor to ensure API paths are handled correctly
httpClient.interceptors.request.use(config => {
  // Only modify the URL if it's a relative URL and not already an API URL
  if (config.url && !config.url.startsWith('http')) {
    // Ensure the URL starts with a slash
    const url = config.url.startsWith('/') ? config.url : `/${config.url}`;
    // If the URL doesn't start with /api, add it
    if (!url.startsWith('/api/')) {
      config.url = `/api${url}`;
    } else {
      config.url = url;
    }
  }
  return config;
});

// Add a request interceptor to add the auth token to requests
httpClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle 401 errors
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is 401 and we haven't already tried to refresh the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const response = await axios.post(
          `${API_CONFIG.BASE_URL}/api/auth/refresh-token`,
          { token: localStorage.getItem('refreshToken') },
          { withCredentials: true }
        );
        
        const { token, refreshToken } = response.data;
        
        // Update the tokens in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
        
        // Update the Authorization header
        originalRequest.headers.Authorization = `Bearer ${token}`;
        
        // Retry the original request
        return httpClient(originalRequest);
      } catch (error) {
        console.error('Error refreshing token:', error);
        // If refresh fails, clear auth data and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        // Optionally show a message (if you use a notification system, add it here)
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

export default httpClient;
